import Link from 'next/link'
import Head from 'next/head'
import jsonApiClient from '../../utils/jsonApiClient'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import TutorProfileSummaryPanel from '../../components/tutorProfile/SummaryPanel'
import {
  faBook,
  faComments,
  faGraduationCap,
  faMapMarkerAlt, faMoneyBill,
  faStar,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons'
import TutorProfileSubjectLevelsList from '../../components/tutorProfile/SubjectLevelsList'
import TutorProfileLocationWithSSR from '../../components/tutorProfile/LocationWithSSR'
import TutorProfileQualificationsList from '../../components/tutorProfile/QualificationsList'
import TutorProfileRateList from '../../components/tutorProfile/RateList'
import ClampLines from 'react-clamp-lines'
import TutorProfileRating from '../../components/tutorProfile/Rating'
import TutorProfileReviewsList from '../../components/tutorProfile/ReviewsList'

export async function getStaticProps({params}) {
 const jsonApi = jsonApiClient()
  const { data: tutorProfile } = await jsonApi.find('tutor-profile', params.slug, {
    include: 'subjects,subject-levels,subject-levels.level,subject-levels.subject,qualifications,' +
      'qualifications.qualification-level'
  })

  const { data: reviews } = await jsonApi.findAll('tutor-review', {
    include: 'client-profile,tutor-profile',
    filter: {
      tutor_profile_id: tutorProfile.id
    },
    page: {
      size: 2
    },
    sort: '-created-at'
  })

  return {
    props: {
      tutorProfile,
      reviews
    },
    revalidate: 60
  }
}

export async function getStaticPaths() {
  const jsonApi = jsonApiClient()
  let profiles = []
  const { data, meta } = await jsonApi.findAll('tutor-profile', {
    page: {
      size: 1000
    }
  })

  return {
    paths: data.map(tutorProfile => (
      {
        params: {
          slug: tutorProfile.slug
        }
      }
    )),
    fallback: 'blocking'
  }
}

export default function TutorsProfile({tutorProfile, reviews}) {
  const showRating = tutorProfile.tutorReviewsCount > 0

  return <>
    <Head>
      <title>{tutorProfile.firstNameAndLastInitial} | Tutors | Tutorspot</title>
    </Head>
    <Container className="pt-3 mb-5 flex-grow-1">
      <Row>
        <Col xs={12} md={3}>
          <Card className="mb-2">
            <Card.Body>
              <TutorProfileSummaryPanel tutorProfile={tutorProfile} />
            </Card.Body>
          </Card>

          {tutorProfile.lessonsInPerson && tutorProfile.locationComplete &&
            <Card className="mb-2">
              <Card.Body>
                <Card.Title>
                  <h4></h4>
                  <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" className="text-success me-2" />
                  Location
                </Card.Title>

                <TutorProfileLocationWithSSR tutorProfile={tutorProfile} />
              </Card.Body>
            </Card>
          }
        </Col>

        <Col xs={12} md={9}>
          <Card className="mb-2">
            <Card.Body>
              <div className="d-md-flex justify-content-between">
                <div className="d-md-flex mb-3 mb-md-0">
                  <h1 className="my-auto mr-md-3 mb-2 mb-md-0">
                    {tutorProfile.firstNameAndLastInitial}
                  </h1>

                  {showRating &&
                    <div className="my-auto ms-1">
                      <TutorProfileRating tutorProfile={tutorProfile} />
                    </div>
                  }
                </div>
                <div className="d-md-flex my-auto">
                  <Link href={`/tutors/${tutorProfile.slug}/send-message`} className="btn btn-primary btn-lg" passHref>
                    <FontAwesomeIcon icon={faComments} className="me-2" />
                    Send Message
                  </Link>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faUserCircle} fixedWidth className="text-success me-2" />
                About {tutorProfile.firstName}
              </Card.Title>

              {tutorProfile.biography &&
                <div className="mb-3">
                  <h5>Biography</h5>
                  <ClampLines text={tutorProfile.biography} lines={5} id="1" moreText="More" lessText="Less" />
                </div>
              }

              {tutorProfile.experience &&
                <div className="mb-3">
                  <h5>Tutoring Experience</h5>
                  <ClampLines text={tutorProfile.experience} lines={5} id="2" moreText="More" lessText="Less" />
                </div>
              }

              {tutorProfile.lessonPlan &&
                <div className="mb-3">
                  <h5>Lesson Planning</h5>
                  <ClampLines text={tutorProfile.lessonPlan} lines={5} id="3" moreText="More" lessText="Less" />
                </div>
              }

              {tutorProfile.examFamiliarity &&
                <div className="mb-3">
                  <h5>Exam Board Experience</h5>
                  <ClampLines text={tutorProfile.examFamiliarity} id="4" moreText="More" lessText="Less" />
                </div>
              }

              {tutorProfile.pastSuccess &&
                <div className="mb-3">
                  <h5>Past Success Stories</h5>
                  <ClampLines text={tutorProfile.pastSuccess} lines={5} id="5" moreText="More" lessText="Less" />
                </div>
              }
            </Card.Body>
          </Card>

          {showRating &&
            <Card className="mb-2">
              <Card.Body>
                <Card.Title>
                  <FontAwesomeIcon icon={faStar} fixedWidth size="lg" className="text-success me-2" />
                  Reviews
                </Card.Title>

                <TutorProfileReviewsList tutorProfile={tutorProfile} reviews={reviews} />
              </Card.Body>
            </Card>
          }

          <Card className="mb-2">
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faBook} fixedWidth size="lg" className="text-success me-2" />
                Subjects
              </Card.Title>

              <TutorProfileSubjectLevelsList tutorProfile={tutorProfile} />
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faGraduationCap} fixedWidth size="lg" className="text-success me-2" />
                Qualifications
              </Card.Title>

              <TutorProfileQualificationsList tutorProfile={tutorProfile} />
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faMoneyBill} fixedWidth size="lg" className="text-success me-2" />
                Rates
              </Card.Title>

              <TutorProfileRateList tutorProfile={tutorProfile} />
            </Card.Body>
          </Card>

          <Card className="mb-2">
            <Card.Body className="d-flex justify-content-center">
              <span className="h3 me-3 my-auto">
                Contact {tutorProfile.firstName} for free
              </span>

              <Link href={`/tutors/${tutorProfile.slug}/send-message`} className="btn btn-primary btn-lg" passHref>
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Send Message
              </Link>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </>
}
